import React from 'react';
import PropTypes from 'prop-types';
import { SweepstakesDrawer } from './SweepstakesDrawer';
import {
  OptInContent,
  SurveyContent,
  DetailsContent,
  StepContent,
  UnauthenticatedContent
} from './content';
import { SWEEPSTAKES_CONSTANTS } from '../../../../core/Constants';
import { useSweepstakesDrawer } from '../../../../contexts/SweepstakesDrawerContext';
import { useSweepstakesData } from '../../../../contexts/SweepstakesDataContext';
import { windowExists } from '../../../../common/utils/AccountUtils';
import { SWEEPSTAKES_MOCK_DATA } from '../../../../core/Data/SweepstakesMockData';
import { mapSweepstakesData } from '../../utils/SweepstakesDataMapper';

// Utility functions
const handleCloseDrawer = () => {
  if (windowExists()) {
    window.LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('loyalty-benefits.close-sweepstakes-drawer');
  }
};

const getDrawerConfig = (drawerType, sweepstakesData, selectedStep, handleStepBack) => {
  // Logo should only show on Details, Step, and Unauthenticated drawers
  // Hero image should only show on Opt-in and Survey drawers
  const showLogo = drawerType === SWEEPSTAKES_CONSTANTS.DRAWER_TYPES.DETAILS
                   || drawerType === SWEEPSTAKES_CONSTANTS.DRAWER_TYPES.STEP
                   || drawerType === SWEEPSTAKES_CONSTANTS.DRAWER_TYPES.UNAUTHENTICATED;
  const showHeroImage = drawerType === SWEEPSTAKES_CONSTANTS.DRAWER_TYPES.OPT_IN
                        || drawerType === SWEEPSTAKES_CONSTANTS.DRAWER_TYPES.SURVEY;

  const baseConfig = {
    drawerTitle: SWEEPSTAKES_CONSTANTS.DRAWER_TITLES.SWEEPSTAKES_DETAILS,
    sweepstakesTitle: sweepstakesData?.title,
    logoUrl: showLogo ? sweepstakesData?.logo : null,
    heroImage: showHeroImage ? sweepstakesData?.heroImage : null,
    onBack: null,
  };

  switch (drawerType) {
  case SWEEPSTAKES_CONSTANTS.DRAWER_TYPES.UNAUTHENTICATED:
  {
    const unauthenticatedData = mapSweepstakesData(SWEEPSTAKES_MOCK_DATA.unauthenticated);

    return {
      ...baseConfig,
      drawerTitle: SWEEPSTAKES_CONSTANTS.DRAWER_TITLES.GIVEAWAY_DETAILS,
      sweepstakesTitle: unauthenticatedData?.title,
      logoUrl: unauthenticatedData?.logo,
      heroImage: null,
    };
  }
  case SWEEPSTAKES_CONSTANTS.DRAWER_TYPES.OPT_IN:
    return {
      ...baseConfig,
      drawerTitle: SWEEPSTAKES_CONSTANTS.DRAWER_TITLES.SWEEPSTAKES_OPT_IN,
      logoUrl: null,
    };
  case SWEEPSTAKES_CONSTANTS.DRAWER_TYPES.SURVEY:
    return {
      ...baseConfig,
      drawerTitle: SWEEPSTAKES_CONSTANTS.DRAWER_TITLES.SWEEPSTAKES_SURVEY,
      sweepstakesTitle: SWEEPSTAKES_CONSTANTS.SWEEPSTAKES_TITLES.SURVEY,
      logoUrl: null,
    };
  case SWEEPSTAKES_CONSTANTS.DRAWER_TYPES.DETAILS:
    return {
      ...baseConfig,
      heroImage: null,
    };
  case SWEEPSTAKES_CONSTANTS.DRAWER_TYPES.STEP:
    return {
      ...baseConfig,
      sweepstakesTitle: selectedStep?.title || baseConfig.sweepstakesTitle,
      logoUrl: baseConfig.logoUrl,
      heroImage: null,
      onBack: handleStepBack,
    };
  default:
    return baseConfig;
  }
};

const getDrawerContent = (
  drawerType,
  optInResult,
  surveyResult,
  detailsResult,
  stepResult,
  unauthenticatedResult
) => {
  switch (drawerType) {
  case SWEEPSTAKES_CONSTANTS.DRAWER_TYPES.UNAUTHENTICATED:
    return { content: unauthenticatedResult.content, footer: unauthenticatedResult.footer };
  case SWEEPSTAKES_CONSTANTS.DRAWER_TYPES.OPT_IN:
    return { content: optInResult.content, footer: optInResult.footer };
  case SWEEPSTAKES_CONSTANTS.DRAWER_TYPES.SURVEY:
    return { content: surveyResult.content, footer: surveyResult.footer };
  case SWEEPSTAKES_CONSTANTS.DRAWER_TYPES.DETAILS:
    return { content: detailsResult.content, footer: detailsResult.footer };
  case SWEEPSTAKES_CONSTANTS.DRAWER_TYPES.STEP:
    return { content: stepResult.content, footer: stepResult.footer };
  default:
    return { content: null, footer: null };
  }
};

export const SweepstakesUnifiedDrawer = () => {
  const {
    drawerState,
    handleStepBack,
  } = useSweepstakesDrawer();

  const {
    sweepstakesData,
    isLoading,
    hasError,
  } = useSweepstakesData();

  const { isOpen, drawerType, selectedStep } = drawerState;

  // Always call all content hooks in the same order to avoid hook order issues
  const unauthenticatedResult = UnauthenticatedContent();
  const optInResult = OptInContent();
  const surveyResult = SurveyContent({
    title: sweepstakesData?.title,
    content: sweepstakesData?.content,
    heroImage: sweepstakesData?.heroImage,
    qualtricsProps: {
      isOpen,
      isSurveyDrawerType: drawerType === SWEEPSTAKES_CONSTANTS.DRAWER_TYPES.SURVEY
    },
  });
  const detailsResult = DetailsContent({ sweepstakesData });
  const stepResult = StepContent({ step: selectedStep });

  const config = getDrawerConfig(drawerType, sweepstakesData, selectedStep, handleStepBack);
  const { content, footer } = getDrawerContent(
    drawerType,
    optInResult,
    surveyResult,
    detailsResult,
    stepResult,
    unauthenticatedResult
  );

  return (
    <SweepstakesDrawer
      isOpen={isOpen}
      onClose={handleCloseDrawer}
      onBack={config.onBack}
      drawerTitle={config.drawerTitle}
      sweepstakesTitle={config.sweepstakesTitle}
      logoUrl={config.logoUrl}
      heroImage={config.heroImage}
      footerContent={footer}
      isLoading={isLoading}
      hasError={hasError}
      testId="sweepstakes-unified-drawer"
    >
      {content}
    </SweepstakesDrawer>
  );
};

SweepstakesUnifiedDrawer.propTypes = {};

SweepstakesUnifiedDrawer.defaultProps = {};